// @ts-check
import classNames from 'classnames';
import React from 'react';
import Alerts from '~/templates/components/Alerts';
import SpeedBump from '~/templates/components/SpeedBump';
import { updateNav } from '~/utils/updateNav';
import AdaSkip from './Core/AdaSkip';
import DesktopHeader from './Core/DesktopHeader/DesktopHeader';
import Footer from './Core/Footer/Footer';
import MobileNav from './Core/MobileHeader/MobileHeader';
import { useGlobalLayout } from './Hocs/useGlobalLayout';

import '../templates/global.scss';
import './animate.css';
import './bootstrap.css';
import './globals.scss';
import './layout.scss';

/**
 * @typedef LayoutLink
 * @property {string} href
 * @property {string} label
 */

/** @type {LayoutLink[]} */
const initialContext = [];
export const BreadcrumbsContext = React.createContext(initialContext);

function runOptimize() {
	// @ts-ignore
	if (!!window?.dataLayer?.push) {
		// @ts-ignore
		window.dataLayer.push({ event: 'optimize.activate' });
	} else {
		return setTimeout(this.runOptimize, 200);
	}
}

/**
 * @param {string} path
 * @returns {{ href: string; label: string }[]} The valid links.
 */
function getPaths(path) {
	const pathSplit = path.split('/').filter((p) => p);

	return pathSplit.map((p, i) => ({
		href: '/' + (i === 0 ? p : pathSplit.slice(0, i + 1).join('/')),
		label: p
			.split('-')
			.map((s) => s.charAt(0).toUpperCase() + s.substring(1))
			.join(' '),
	}));
}

/**
 *
 * @param {Location} location
 * @returns {boolean} A value indicating whether the breadcrumbs should be shown.
 */
function shouldShowBreadcrumbs(location) {
	if (!location?.pathname) {
		return false;
	}

	// No breadcrumbs if there are only one level, or if it's login page.
	const paths = getPaths(location.pathname);

	return !(paths.length === 1 || paths?.[0]?.href === '/login');
}

/** @param {Location} [location] */
function attachUtmParametersToLinkTags(location) {
	if (!location?.search) {
		return;
	}

	document.querySelectorAll('a').forEach((tag, i) => {
		const isLocal = tag.href.startsWith('http://localhost:8000');
		const isOzk = !!tag.href.match(/(.ozk.)/);

		if (isLocal || isOzk) {
			// Create URL from tag's href, then add each param from our Location, and set as tag's href.
			const url = new URL(tag.href);
			const locationParams = new URLSearchParams(location.search);
			locationParams.forEach((val, key) => url.searchParams.append(key, val));
			tag.href = url.href;
		}
	});
}

/**
 * @typedef LayoutProps
 * @property {string} [titleTemplate]
 * @property {Location} [location]
 */
/** @type {import('~/types/React').ReactFC<LayoutProps>} */
const Layout = (props) => {
	const { location, id, ...rest } = props;

	const globalLayout = useGlobalLayout();

	// On Mount
	React.useEffect(() => {
		runOptimize();

		attachUtmParametersToLinkTags(location);

		updateNav();
	}, [location]);

	const breadcrumbsValue = shouldShowBreadcrumbs(location) ? getPaths(location.pathname) : [];

	return (
		<React.Fragment>
			<AdaSkip />

			<SpeedBump />

			<MobileNav currentPath={location?.pathname} options={globalLayout?.header} />

			<Alerts />

			<DesktopHeader currentPath={location?.pathname} options={globalLayout?.header} />

			<BreadcrumbsContext.Provider value={breadcrumbsValue}>
				<div id="main" className={classNames('PageLayout', 'animated fadeIn', rest.className)}>
					{rest.children}
				</div>
			</BreadcrumbsContext.Provider>

			<Footer options={globalLayout?.footer} />
		</React.Fragment>
	);
};

export default Layout;
